import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Grant, ListedGroup } from "../../../api/access/accessModel";
import Chip from "../../../ui/feedback/Chip";
import { itemKindText } from "../../workflows";
import { curatedKinds, permissionsOf } from "../model/grantSets";
import { isInstallationPermission } from "../model/permissionCatalogue";

interface GrantChipsProps {
  group: ListedGroup;
  /** The project whose list shows it; `null` in the installation's list. */
  projectId: string | null;
  itemKinds: readonly string[];
  /** The group around it, for a group inside another: "everything Historians has". */
  parentName: string | null;
}

/**
 * What a group allows, in short, on its row: in a project what it gives there, an included
 * installation group's own part then what the project switched on or off; in the
 * installation its installation permissions, then what it brings to projects.
 */
export default function GrantChips({ group, projectId, itemKinds, parentName }: GrantChipsProps) {
  const { t } = useTranslation();
  const included = projectId !== null && group.projectId === null;
  const on = group.overrides.filter((override) => override.override === "on");
  const off = group.overrides.filter((override) => override.override === "off");
  const installation = group.grants.filter((grant) => isInstallationPermission(grant.permission));
  const project = group.grants.filter((grant) => !isInstallationPermission(grant.permission));
  return (
    // Narrow screens show a group's name and size only, as the design's narrow list does; its screen has the rest.
    <span className="mt-1 hidden flex-wrap items-center gap-1 text-xs sm:flex">
      {parentName === null ? null : <span className="app-text-muted">{t("access.chips.everythingOf", { group: parentName })}</span>}
      {group.system === "administrators" && projectId === null ? <Chip>{t("access.chips.everything")}</Chip> : included ? (
        <>
          <Chips grants={project.filter((grant) => !off.some((override) => override.permission === grant.permission))} itemKinds={itemKinds} t={t} />
          <Labelled label={t("access.chips.onHere")} grants={on} itemKinds={itemKinds} t={t} />
          <Labelled label={t("access.chips.offHere")} grants={off} itemKinds={itemKinds} t={t} />
        </>
      ) : projectId !== null ? (
        <Chips grants={group.effective} itemKinds={itemKinds} t={t} />
      ) : (
        <>
          <Chips grants={installation} itemKinds={itemKinds} t={t} />
          <Labelled label={t("access.chips.inProjects")} grants={project} itemKinds={itemKinds} t={t} />
        </>
      )}
    </span>
  );
}

function Labelled({ label, grants, itemKinds, t }: { label: string; grants: readonly Grant[]; itemKinds: readonly string[]; t: TFunction }) {
  if (grants.length === 0) return null;
  return (
    <>
      <span className="app-text-muted">{label}</span>
      <Chips grants={grants} itemKinds={itemKinds} t={t} />
    </>
  );
}

function Chips({ grants, itemKinds, t }: { grants: readonly Grant[]; itemKinds: readonly string[]; t: TFunction }) {
  return permissionsOf(grants).map((permission) => (
    <Chip key={permission}>{permission === "curate" ? curateText(t, curatedKinds(grants, itemKinds), itemKinds) : t(`access.short.${permission}`)}</Chip>
  ));
}

/** A set of grants in short, as a group's row shows them: "View", "Propose", "Curate every kind". */
export function PermissionChips({ grants, itemKinds }: { grants: readonly Grant[]; itemKinds: readonly string[] }) {
  const { t } = useTranslation();
  return <Chips grants={grants} itemKinds={itemKinds} t={t} />;
}

/** "Curate every kind", "Curate competency questions" or "Curate 2 kinds". */
function curateText(t: TFunction, kinds: readonly string[], itemKinds: readonly string[]): string {
  if (kinds.length === itemKinds.length) return t("access.chips.curateEvery");
  const [only] = kinds;
  if (kinds.length === 1 && only !== undefined) return t("access.chips.curateOne", { kind: itemKindText(only, t) });
  return t("access.chips.curateSome", { count: kinds.length });
}
